import React from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { useApiQuery } from "@/hooks/useApi";
import { StatusChip, ModeIcon, formatDate, formatDateShort, formatMoney, Kpi } from "@/components/ventrix/atoms";
import { Package, AlertOctagon, FileText, DollarSign, ArrowRight, MapPin } from "lucide-react";

export default function Dashboard() {
  const shipsQ = useApiQuery("dash-ships", "/v1/shipments?limit=50");
  const invsQ = useApiQuery("dash-invs", "/v1/invoices?limit=200");
  const statsQ = useApiQuery("dash-stats", "/v1/shipments/stats");
  // null while loading (drives skeletons); empty on error so nothing hangs.
  const ships = shipsQ.isPending ? null : (shipsQ.data ?? { items: [] });
  const invs = invsQ.isPending ? null : (invsQ.data ?? { items: [] });
  const stats = statsQ.data ?? null;

  const active = (ships?.items || []).filter(s => s.status !== "DELIVERED");
  const exceptions = active.filter(s => s.status === "EXCEPTION");
  const inTransit = active.filter(s => ["PICKED_UP","IN_TRANSIT","AT_HUB","CUSTOMS","OUT_FOR_DELIVERY"].includes(s.status));
  const invItems = invs?.items || [];
  const invCount = invs?.total ?? invItems.length;
  const openInvoices = invItems.filter(i => i.status !== "paid");
  const spend = invItems.reduce((sum, i) => sum + (i.amount || 0), 0);
  // Reflect payments: outstanding is the sum of remaining balances (total minus
  // what's been paid), not the full invoice totals.
  const paid = invItems.reduce((sum, i) => sum + (i.amount_paid || 0), 0);
  const outstanding = openInvoices.reduce((s, i) => s + Math.max(0, (i.amount || 0) - (i.amount_paid || 0)), 0);

  return (
    <PortalLayout section="customer">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="vtx-eyebrow">/ Overview</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Ventrix control tower</h1>
        </div>
        <Link to="/app/shipments" className="vtx-btn-primary" data-testid="dashboard-view-shipments">View shipments</Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Active shipments" value={stats?.active ?? active.length} Icon={Package} accent="#0E2F76" />
        <Kpi label="Exceptions" value={stats?.exceptions ?? exceptions.length} Icon={AlertOctagon} accent="#d41f26" />
        <Kpi label="Open invoices" value={openInvoices.length} Icon={FileText} accent="#B45309" />
        <Kpi label="Outstanding" value={formatMoney(outstanding)} Icon={DollarSign} accent="#0E2F76" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 vtx-card">
          <div className="flex items-center justify-between p-5 border-b border-[#A9C0E0]/40">
            <div className="vtx-eyebrow">In transit</div>
            <Link to="/app/shipments" className="text-xs font-semibold text-[#0E2F76] hover:text-[#d41f26] inline-flex items-center gap-1" data-testid="dashboard-see-all-shipments">See all <ArrowRight size={12}/></Link>
          </div>
          {ships === null && <div className="p-6 space-y-3">{[1,2,3].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
          {ships && inTransit.length === 0 && (
            <div className="p-10 text-center text-sm text-[#475569]">
              No shipments in transit right now.
            </div>
          )}
          {ships && inTransit.length > 0 && (
            <div className="overflow-x-auto"><table className="vtx-table w-full">
              <thead><tr><th>Tracking</th><th>Route</th><th>Mode</th><th>Status</th><th>ETA</th></tr></thead>
              <tbody>
                {inTransit.slice(0, 6).map(s => (
                  <tr key={s.id} data-testid={`dashboard-row-${s.tracking_number}`}>
                    <td><Link to={`/app/shipments/${s.id}`} className="font-mono text-[#0E2F76] font-semibold hover:text-[#d41f26]">{s.tracking_number}</Link></td>
                    <td className="text-[#475569]">{s.origin} → {s.destination}</td>
                    <td><ModeIcon mode={s.mode} showLabel /></td>
                    <td><StatusChip status={s.status} /></td>
                    <td className="font-mono text-[#0E2F76]">{formatDateShort(s.eta)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
        <div className="vtx-card">
          <div className="p-5 border-b border-[#A9C0E0]/40 flex items-center justify-between">
            <div className="vtx-eyebrow">Attention needed</div>
            <span className="text-[10px] font-bold text-[#d41f26]">{exceptions.length} open</span>
          </div>
          <div className="p-5 space-y-3">
            {exceptions.length === 0 && <div className="text-sm text-[#475569]">Everything is on-track. Nice.</div>}
            {exceptions.slice(0, 5).map(s => (
              <Link key={s.id} to={`/app/shipments/${s.id}`} className="block p-3 border border-[#d41f26]/30 bg-[#FEE2E2]/40 hover:bg-[#FEE2E2]" data-testid={`exception-${s.tracking_number}`}>
                <div className="font-mono text-sm text-[#0E2F76] font-semibold">{s.tracking_number}</div>
                <div className="text-xs text-[#475569] mt-0.5">{s.origin} → {s.destination}</div>
                <div className="text-xs font-bold text-[#d41f26] mt-1 inline-flex items-center gap-1"><AlertOctagon size={11}/> Requires action</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="vtx-card">
          <div className="p-5 border-b border-[#A9C0E0]/40 flex items-center justify-between">
            <div className="vtx-eyebrow">Recent invoices</div>
            <Link to="/app/invoices" className="text-xs font-semibold text-[#0E2F76] hover:text-[#d41f26]" data-testid="dashboard-see-all-invoices">See all</Link>
          </div>
          {invs === null && <div className="p-6 space-y-3">{[1,2,3].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
          {invs && (
            <div className="overflow-x-auto"><table className="vtx-table w-full">
              <thead><tr><th>Invoice</th><th>Amount</th><th>Balance</th><th>Status</th><th>Due</th></tr></thead>
              <tbody>
                {(invs.items || []).slice(0, 5).map(i => {
                  const bal = Math.max(0, (i.amount || 0) - (i.amount_paid || 0));
                  const part = (i.amount_paid || 0) > 0 && i.status !== "paid";
                  return (
                  <tr key={i.id}>
                    <td className="font-mono text-[#0E2F76] font-semibold">{i.invoice_number}</td>
                    <td>{formatMoney(i.amount, i.currency)}
                      {part && <div className="text-[10px] text-[#475569]">Paid {formatMoney(i.amount_paid, i.currency)}</div>}
                    </td>
                    <td className="font-semibold">{i.status === "paid" ? <span className="text-[#166534]">Paid</span> : formatMoney(bal, i.currency)}</td>
                    <td><StatusChip status={i.status} /></td>
                    <td className="font-mono text-[#475569]">{formatDateShort(i.due_date)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
        <div className="vtx-card p-6 bg-[#0E2F76] text-white relative overflow-hidden">
          <div className="absolute inset-0 vtx-hero-grid opacity-25" />
          <div className="relative">
            <div className="vtx-eyebrow text-[#A9C0E0]">Billed total</div>
            <div className="mt-2 text-4xl font-black tracking-tighter">{formatMoney(spend)}</div>
            <div className="mt-1 text-xs text-[#A9C0E0]">Across {invCount} invoice{invCount === 1 ? "" : "s"}</div>
            <div className="mt-6 flex items-center justify-between">
              <span className="text-xs text-[#A9C0E0]">Paid</span>
              <span className="font-mono font-bold text-[#86efac]" data-testid="dashboard-paid">{formatMoney(paid)}</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between">
              <span className="text-xs text-[#A9C0E0]">Outstanding</span>
              <span className="font-mono font-bold" data-testid="dashboard-outstanding">{formatMoney(outstanding)}</span>
            </div>
            {/* Payment progress: how much of what you've been billed is paid. */}
            <div className="h-1.5 mt-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#22c55e]" style={{ width: `${spend ? Math.min(100, (paid / spend) * 100) : 0}%` }} />
            </div>
            <div className="mt-1 text-[10px] text-[#A9C0E0]">{spend ? Math.round((paid / spend) * 100) : 0}% paid</div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
